/**
 * The exam session's localStorage mirror (brief §exam: "localStorage mirror
 * keyed by session id with 'återställ osparade svar' prompt when newer").
 * Pure data helpers — `SessionPage.tsx` owns the debounce and the prompt UI.
 */
import type { AnswerEntry } from "./types";

const STORAGE_PREFIX = "mf.exam.session.";
// Explicit auth cleanup invalidates pending callbacks synchronously, before
// React has committed the signed-out state and unmounted protected pages.
let cleanupGeneration = 0;
export function examDraftGeneration(): number {
  return cleanupGeneration;
}

export interface ExamDraft {
  /** The server `revision` this draft's answers were saved on top of. */
  baseRevision: number;
  answers: Record<string, AnswerEntry>;
  updatedAt: number;
}

function key(sessionId: string): string {
  return `${STORAGE_PREFIX}${sessionId}`;
}

export function saveDraft(sessionId: string, draft: ExamDraft): void {
  try {
    window.localStorage.setItem(key(sessionId), JSON.stringify(draft));
  } catch {
    /* storage full or unavailable (private mode) — autosave to the server still works */
  }
}

export function loadDraft(sessionId: string): ExamDraft | null {
  try {
    const raw = window.localStorage.getItem(key(sessionId));
    if (!raw) return null;
    return JSON.parse(raw) as ExamDraft;
  } catch {
    return null;
  }
}

export function clearDraft(sessionId: string): void {
  try {
    window.localStorage.removeItem(key(sessionId));
  } catch {
    /* ignore */
  }
}

/** Sign-out / account deletion: no answer draft may outlive the signed-in user on this browser. */
export function clearAllExamDrafts(): void {
  cleanupGeneration += 1;
  try {
    const storage = window.localStorage;
    for (let i = storage.length - 1; i >= 0; i--) {
      const name = storage.key(i);
      if (name?.startsWith(STORAGE_PREFIX)) storage.removeItem(name);
    }
  } catch {
    /* ignore */
  }
}

/**
 * True when the draft holds edits the server has never seen: it was taken at
 * the server's current revision (nothing newer has landed since — from this
 * tab or another) yet its answers differ from what the server has stored.
 */
export function hasUnsavedChanges(
  draft: ExamDraft | null,
  serverRevision: number,
  serverAnswers: Record<string, AnswerEntry>,
): boolean {
  if (!draft) return false;
  if (draft.baseRevision !== serverRevision) return false;
  return JSON.stringify(draft.answers) !== JSON.stringify(serverAnswers);
}
